import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import { collection, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import AdminSidebar from '../components/AdminSidebar';
import 'leaflet/dist/leaflet.css';
import '../pages/DriverTracking.css';
import carGreen from '../assets/1.png';
import carBlue from '../assets/2.png';
import carRed from '../assets/3.png';

const timeSince = (date) => {
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
  if (seconds < 60) return `${seconds} ثانية`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)} دقيقة`;
  return `${Math.floor(seconds / 3600)} ساعة`;
};


// Car icon definitions
const carIcons = {
  green: new L.Icon({ iconUrl: carGreen, iconSize: [50, 50], iconAnchor: [25, 50], popupAnchor: [0, -40] }),
  blue: new L.Icon({ iconUrl: carBlue, iconSize: [50, 50], iconAnchor: [25, 50], popupAnchor: [0, -40] }),
  red: new L.Icon({ iconUrl: carRed, iconSize: [50, 50], iconAnchor: [25, 50], popupAnchor: [0, -40] }),
};

// Function to determine the marker color based on driver status
const getMarkerColor = (driver) => {
  if (!driver.isMoving) return 'red';
  if (driver.orderStatus === 'في الطريق') return 'blue';
  return 'green';
};

const DriverTracking = () => {
  const [drivers, setDrivers] = useState([]);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [messageBoxes, setMessageBoxes] = useState({});
  const [sending, setSending] = useState(false);
  const [activeTab, setActiveTab] = useState('map'); // 'map' or 'list'

  // Fetch drivers data
  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, 'drivers'), (snapshot) => {
      const updatedDrivers = snapshot.docs.map(doc => {
        const data = doc.data();
        const location = data.location;
    
        if (location?.lat && location?.lng) {
          const lastUpdate = data.lastUpdate?.toDate() || new Date();
          const isOffline = (Date.now() - lastUpdate.getTime()) > 60000; // أكثر من 60 ثانية
    
          return {
            id: doc.id,
            name: data.name,
            phone: data.phone,
            location: { lat: location.lat, lng: location.lng },
            status: data.status || 'offline',
            isAvailable: data.isAvailable ?? false,
            orderStatus: data.orderStatus || 'لا يوجد طلب',
            speed: data.speed || 0,
            isMoving: data.isMoving ?? false,
            fcmToken: data.fcmToken || '',
            lastUpdate: lastUpdate,
            isOffline: isOffline, // ✅ مضاف حديثًا
          };
        }
    
        return null;
      }).filter(Boolean);
    
      setDrivers(updatedDrivers);
    });
    

    return () => unsubscribe();
  }, []);

  // Handle message input changes
  const handleMessageChange = (driverId, text) => {
    setMessageBoxes(prev => ({ ...prev, [driverId]: text }));
  };

  // Send message to driver
  const handleSendMessage = async (driverId) => {
    const message = messageBoxes[driverId];
    if (!message?.trim()) {
      alert('الرجاء إدخال نص الرسالة');
      return;
    }

    const driver = drivers.find(d => d.id === driverId);
    if (!driver?.fcmToken) {
      alert("❌ هذا السائق لا يحتوي على FCM Token");
      return;
    }

    setSending(true);
    try {
      const res = await fetch("https://us-central1-atdelivery-9d139.cloudfunctions.net/sendOrderNotification", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          fcmToken: driver.fcmToken,
          title: "رسالة من الإدارة",
          body: message,
          type: "admin_message"
        }),
      });
      

      const data = await res.json();
      if (data.success) {
        alert("✅ تم إرسال الرسالة بنجاح");
        setMessageBoxes(prev => ({ ...prev, [driverId]: '' }));
        setSelectedDriver(null);
      } else {
        alert("❌ حدث خطأ أثناء الإرسال");
      }
    } catch (e) {
      console.error("Send error", e);
      alert("❌ حدث خطأ تقني");
    } finally {
      setSending(false);
    }
  };

  

  return (
    <div className="driver-tracking-layout">
      <AdminSidebar />
      <div className="driver-tracking-content">
        <div className="driver-tracking-header">
          <h1>تتبع السائقين</h1>
          <div className="tab-selector">
            <button 
              className={`tab-button ${activeTab === 'map' ? 'active' : ''}`} 
              onClick={() => setActiveTab('map')}
            >
              الخريطة
            </button>
            <button 
              className={`tab-button ${activeTab === 'list' ? 'active' : ''}`} 
              onClick={() => setActiveTab('list')}
            >
              قائمة السائقين
            </button>
          </div>
        </div>

        <div className="dashboard-container">
          {activeTab === 'map' ? (
            <div className="map-container">
              <div className="map-legend">
                <div className="legend-item">
                  <div className="legend-color green"></div>
                  <span>متاح</span>
                </div>
                <div className="legend-item">
                  <div className="legend-color blue"></div>
                  <span>في الطريق</span>
                </div>
                <div className="legend-item">
                  <div className="legend-color red"></div>
                  <span>متوقف</span>
                </div>
              </div>
              <MapContainer center={[29.3759, 47.9774]} zoom={12} className="leaflet-map">
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                {drivers.map(driver => (
                  <Marker
                    key={driver.id}
                    position={driver.location}
                    icon={carIcons[getMarkerColor(driver)]}
                  >
                    <Popup>
                      <div className="driver-popup">
                        <h3>{driver.name}</h3>
                        <div className="popup-data">
                          <div className="popup-row">
                            <span className="popup-label">رقم الهاتف:</span>
                            <span className="popup-value">{driver.phone}</span>
                          </div>
                          <div className="popup-row">
                            <span className="popup-label">حالة الطلب:</span>
                            <span className="popup-value">{driver.orderStatus}</span>
                          </div>
                          <div className="popup-row">
                            <span className="popup-label">السرعة:</span>
                            <span className="popup-value">{driver.speed.toFixed(1)} كم/س</span>
                          </div>
                          <div className="popup-row">
                            <span className="popup-label">الحالة:</span>
                            <span className="popup-value">
  {driver.isOffline ? '🚫 غير متصل' : (driver.isMoving ? '✅ يتحرك' : '⏸️ متوقف')}
</span>
                            <span className="popup-value">{driver.isMoving ? 'يتحرك' : 'متوقف'}</span>
                          </div>
                          <div className="popup-row">
                            <span className="popup-label">آخر تحديث:</span>
                            <span className="popup-value">{timeSince(driver.lastUpdate)}</span>
                            <span className="popup-value">{driver.lastUpdate.toLocaleString('ar-EG')}</span>
                          </div>
                        </div>
                        <button 
                          className="popup-button"
                          onClick={() => {
                            setSelectedDriver(driver);
                            setActiveTab('list');
                          }}
                        >
                          إرسال رسالة
                        </button>
                      </div>
                    </Popup>
                  </Marker>
                ))}
              </MapContainer>
            </div>
          ) : (
            <div className="driver-list-section">
              <div className="card">
                <div className="card-header">
                  <h2>بيانات السائقين</h2>
                  <div className="driver-stats">
                    <div className="stat">
                      <span className="stat-value">{drivers.length}</span>
                      <span className="stat-label">إجمالي السائقين</span>
                    </div>
                    <div className="stat">
                      <span className="stat-value">{drivers.filter(d => d.isMoving).length}</span>
                      <span className="stat-label">يتحركون حالياً</span>
                    </div>
                    <div className="stat">
                      <span className="stat-value">{drivers.filter(d => d.orderStatus !== 'لا يوجد طلب').length}</span>
                      <span className="stat-label">لديهم طلبات</span>
                    </div>
                  </div>
                </div>

                {drivers.length === 0 ? (
                  <div className="no-data">
                    <div className="no-data-icon">🚫</div>
                    <p>لا يوجد سائقين متاحين حالياً.</p>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="driver-table">
                      <thead>
                        <tr>
                          <th>الاسم</th>
                          <th>الهاتف</th>
                          <th>الحالة</th>
                          <th>السرعة</th>
                          <th>حالة الطلب</th>
                          <th>آخر تحديث</th>
                          <th>الإجراءات</th>
                        </tr>
                      </thead>
                      <tbody>
                        {drivers.map((driver) => (
                          <React.Fragment key={driver.id}>
                            <tr className={selectedDriver?.id === driver.id ? 'selected-row' : ''}>
                              <td>{driver.name}</td>
                              <td>{driver.phone}</td>
                              <td>
                                <span className={`status-badge ${driver.isMoving ? 'moving' : 'stopped'}`}>
                                  {driver.isMoving ? 'يتحرك' : 'متوقف'}
                                </span>
                              </td>
                              <td>
                                <div className="speed-indicator">
                                  <div className="speed-value">{driver.speed.toFixed(1)}</div>
                                  <div className="speed-unit">كم/س</div>
                                </div>
                              </td>
                              <td>
                                <span className={`order-status ${driver.orderStatus === 'لا يوجد طلب' ? 'no-order' : 'has-order'}`}>
                                  {driver.orderStatus}
                                </span>
                              </td>
                              <td className="last-update">{timeSince(driver.lastUpdate)}</td>
                              <td className="last-update">{driver.lastUpdate.toLocaleString('ar-EG')}</td>
                              <td>
                                <button
                                  className="action-button"
                                  onClick={() => setSelectedDriver(selectedDriver?.id === driver.id ? null : driver)}
                                >
                                  {selectedDriver?.id === driver.id ? 'إلغاء' : 'إرسال رسالة'}
                                </button>
                              </td>
                            </tr>
                            {selectedDriver?.id === driver.id && (
                              <tr className="message-row">
                                <td colSpan="7">
                                  <div className="message-container">
                                    <textarea
                                      value={messageBoxes[driver.id] || ''}
                                      onChange={(e) => handleMessageChange(driver.id, e.target.value)}
                                      placeholder="اكتب رسالتك هنا..."
                                      className="message-textarea"
                                    />
                                    <div className="message-actions">
                                      <button
                                        onClick={() => handleSendMessage(driver.id)}
                                        disabled={sending}
                                        className={`send-button ${sending ? 'sending' : ''}`}
                                      >
                                        {sending ? 'جاري الإرسال...' : 'إرسال'}
                                      </button>
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DriverTracking;